import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import ModalCloseX from "../components/common/ModalCloseX";
import {
  listAdminUsers,
  listAdminBusinesses,
  updateAdminUser,
  updateAdminUserBusinesses,
  resetAdminUserPassword,
} from "../api/adminUsers";

const ROLE_OPTIONS = ["admin", "owner", "host", "manager", "technician"];

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-between bg-rose-500 px-6 py-4">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <ModalCloseX onClick={onClose} />
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
}


export default function AdminUsers() {
  const { user, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businessLoading, setBusinessLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({
    full_name: "",
    role: ROLE_OPTIONS[0],
    is_active: true,
  });
  const [editSaving, setEditSaving] = useState(false);

  const [businessTarget, setBusinessTarget] = useState(null);
  const [selectedBusinessIds, setSelectedBusinessIds] = useState([]);
  const [businessSaving, setBusinessSaving] = useState(false);

  const [passwordTarget, setPasswordTarget] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    password: "",
    confirm: "",
  });
  const [passwordSaving, setPasswordSaving] = useState(false);

  const isSuperAdmin = Boolean(user?.is_super_admin);

  useEffect(() => {
    if (authLoading || !isSuperAdmin) return;
    let cancelled = false;
    setError("");
    setStatusMessage("");
    setLoading(true);
    setBusinessLoading(true);

    const loadUsers = async () => {
      try {
        const [usersRes, businessesRes] = await Promise.all([
          listAdminUsers(),
          listAdminBusinesses(),
        ]);
        if (cancelled) return;
        setUsers(Array.isArray(usersRes) ? usersRes : []);
        setBusinesses(Array.isArray(businessesRes) ? businessesRes : []);
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setError(err?.message || "Unable to load users.");
      } finally {
        if (!cancelled) {
          setLoading(false);
          setBusinessLoading(false);
        }
      }
    };

    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isSuperAdmin]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) => {
      const haystack = `${u.email || ""} ${u.full_name || ""}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [users, searchTerm]);

  const handleEditOpen = (userRow) => {
    setEditTarget(userRow);
    setEditForm({
      full_name: userRow.full_name || "",
      role: userRow.role || ROLE_OPTIONS[0],
      is_active: Boolean(userRow.is_active),
    });
  };

  const handleEditSave = async (event) => {
    event.preventDefault();
    if (!editTarget) return;
    setEditSaving(true);
    setError("");
    setStatusMessage("");
    try {
      const payload = {
        full_name: editForm.full_name.trim() || null,
        role: editForm.role,
        is_active: editForm.is_active,
      };
      const updated = await updateAdminUser(editTarget.id, payload);
      setUsers((prev) =>
        prev.map((u) => (u.id === updated.id ? updated : u))
      );
      setStatusMessage("User updated.");
      setEditTarget(null);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to update user.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleBusinessOpen = (userRow) => {
    setBusinessTarget(userRow);
    setSelectedBusinessIds((userRow.businesses || []).map((b) => b.business_id));
  };

  const handleBusinessSave = async (event) => {
    event.preventDefault();
    if (!businessTarget) return;
    setBusinessSaving(true);
    setError("");
    setStatusMessage("");
    try {
      const updated = await updateAdminUserBusinesses(
        businessTarget.id,
        selectedBusinessIds
      );
      setUsers((prev) =>
        prev.map((u) => (u.id === updated.id ? updated : u))
      );
      setStatusMessage("Business links updated.");
      setBusinessTarget(null);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to update business links.");
    } finally {
      setBusinessSaving(false);
    }
  };

  const handlePasswordOpen = (userRow) => {
    setPasswordTarget(userRow);
    setPasswordForm({ password: "", confirm: "" });
  };

  const handlePasswordSave = async (event) => {
    event.preventDefault();
    if (!passwordTarget) return;
    if (passwordForm.password !== passwordForm.confirm) {
      setError("Passwords must match.");
      return;
    }
    if (!passwordForm.password) {
      setError("Enter a new password.");
      return;
    }
    setPasswordSaving(true);
    setError("");
    try {
      await resetAdminUserPassword(passwordTarget.id, passwordForm.password);
      setStatusMessage("Password reset.");
      setPasswordTarget(null);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to reset password.");
    } finally {
      setPasswordSaving(false);
    }
  };

  if (authLoading || (isSuperAdmin && loading)) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-3 sm:px-0">
        <h1 className="text-2xl font-semibold mb-4">User Management</h1>
        <p className="text-slate-600">Loading users…</p>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 text-center">
        <h1 className="text-2xl font-semibold text-slate-900 mb-2">
          Access Denied
        </h1>
        <p className="text-sm text-slate-600">
          You do not have permission to view this page.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-3 sm:px-0 space-y-6">
      <div>
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold text-slate-900">User Management</h1>
          <p className="text-sm text-slate-600">
            List and manage accounts across the Ellyanna system.
          </p>
        </div>
        {error && (
          <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {statusMessage && (
          <div className="mt-4 rounded border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
            {statusMessage}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-semibold text-slate-700">
          Search:
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Email or name"
            className="ml-2 rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none"
          />
        </label>
        <span className="text-xs text-slate-500">
          {users.length} users across {businesses.length} businesses
        </span>
      </div>

      <div className="overflow-x-auto rounded border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Role</th>
              <th className="px-4 py-2 text-left">Active</th>
              <th className="px-4 py-2 text-left">Businesses</th>
              <th className="px-4 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filteredUsers.map((row) => (
              <tr key={row.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{row.email}</td>
                <td className="px-4 py-3 text-slate-700">
                  {row.full_name || "—"}
                </td>
                <td className="px-4 py-3 text-slate-700 capitalize">{row.role || "—"}</td>
                <td className="px-4 py-3">
                  {row.is_active ? "Yes" : "No"}
                </td>
                <td className="px-4 py-3 space-y-1">
                  {row.businesses?.length ? (
                    <div className="flex flex-wrap gap-1">
                      {row.businesses.map((biz) => (
                        <span
                          key={`${row.id}-${biz.business_id}`}
                          className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600"
                          title={biz.business_name}
                        >
                          ID: {biz.business_id} ({biz.business_code || "--"})
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">None</span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-700 space-x-2">
                  <button
                    type="button"
                    onClick={() => handleEditOpen(row)}
                    className="rounded border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBusinessOpen(row)}
                    className="rounded border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Businesses
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePasswordOpen(row)}
                    className="rounded border border-rose-400 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                  >
                    Reset Password
                  </button>
                </td>
              </tr>
            ))}
            {!filteredUsers.length && (
              <tr>
                <td
                  className="px-4 py-5 text-center text-sm text-slate-500"
                  colSpan={6}
                >
                  No users match your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editTarget && (
        <Modal title="Edit User" onClose={() => setEditTarget(null)}>
          <form onSubmit={handleEditSave} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Email
              </label>
              <div className="text-sm text-slate-800">{editTarget.email}</div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Full name
              </label>
              <input
                type="text"
                value={editForm.full_name}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    full_name: e.target.value,
                  }))
                }
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Role
              </label>
              <select
                value={editForm.role}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, role: e.target.value }))
                }
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
              >
                {ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>
            <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={editForm.is_active}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, is_active: e.target.checked }))
                }
                className="h-4 w-4 rounded border-slate-300"
              />
              Active
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditTarget(null)}
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={editSaving}
                className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {editSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {businessTarget && (
        <Modal
          title="Manage Business Links"
          onClose={() => setBusinessTarget(null)}
        >
          <form onSubmit={handleBusinessSave} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">
                Assign businesses for {businessTarget.email}
              </label>
              {businessLoading ? (
                <p className="text-xs text-slate-500">Loading businesses…</p>
              ) : (
                <select
                  multiple
                  size={Math.min(8, businesses.length || 1)}
                  value={selectedBusinessIds.map(String)}
                  onChange={(e) => {
                    const options = Array.from(e.target.selectedOptions);
                    setSelectedBusinessIds(options.map((opt) => Number(opt.value)));
                  }}
                  className="w-full min-h-[150px] rounded border border-slate-300 bg-white px-2 py-2 text-sm text-slate-700 focus:border-slate-500 focus:outline-none"
                >
                  {businesses.map((biz) => (
                    <option key={biz.id} value={biz.id}>
                      {biz.code} — {biz.name || `Business ${biz.id}`}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setBusinessTarget(null)}
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={businessSaving}
                className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {businessSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {passwordTarget && (
        <Modal
          title="Reset Password"
          onClose={() => setPasswordTarget(null)}
        >
          <form onSubmit={handlePasswordSave} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Email
              </label>
              <div className="text-sm text-slate-800">{passwordTarget.email}</div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={passwordForm.password}
                onChange={(e) =>
                  setPasswordForm((prev) => ({ ...prev, password: e.target.value }))
                }
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                value={passwordForm.confirm}
                onChange={(e) =>
                  setPasswordForm((prev) => ({ ...prev, confirm: e.target.value }))
                }
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPasswordTarget(null)}
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={passwordSaving}
                className="rounded bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {passwordSaving ? "Saving..." : "Reset Password"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
